import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MessageChat from './message-chat';
import { selectUserId } from '../../../../../../features/auth/auth-slice';
import { useGroupChat } from '../../../../../../hooks/actions/use-group-chat';
import { CloseIcon } from '../../../../../../assets/icon/svg';
import { useMessagesSocket } from '../../../../../../hooks/socket/use-messages-socket';
import LoadingState from '../../../../../ui/loadibg-state';
import ErrorState from '../../../../../ui/error-state';
import ChatAttachmentModal from './chat-attachment-modal';
import ChatsHeader from './chats-header';
import ConfirmDefaultModal from '../../../../../global/modal/confirm-default-modal';
import { clearChatValue, selectChats, setChatValue, } from '../../../../../../features/chats/chats-slice';
import { getFileIcon, isImageFile } from '../../../../../ui/get-file-icon';
import { getMessageAttachment } from '../../../../../../utils/get-message-attachment';
import { useProposalTypeData } from '../../../../../../hooks/proposal/use-proposal-type-data';
import ChatAttachmentPreview from './chat-attachment-preview';
import { useModalAccess } from '../../../../../global/modal/use-modal-access';
import { useAuthDataState } from '../../../../../../hooks/data-state/use-auth-data-state';

interface Props {
    shouldScrollToBottom?: boolean;
}

export default function ProposalGroupChatModal({ shouldScrollToBottom = false, }: Props) {
    
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);
    const { isDraftProposal } = useProposalTypeData()
    const { isCancelUpdateMessage } = useModalAccess()
    const { selectedFilePreviewUrl } = useSelector(selectChats);
    const { isUserMonitoring } = useAuthDataState();
    
    const {
        fileInputRef,
        showAttachments,
        isSearchOpen,
        groupMessages,
        isLoadingGroupMessages,
        isErrorGroupMessages,
        chatEndRef,
        activeMessageId,
        message,
        handleCancelUpdateComment,
        isLoadingAddCommentChat,
        isLoadingDeleteComment,
        deletingMessageId,
        isOpenDeleteModal,
        handleClose,
        handleDeleteComment,
        handleDownloadAttachment,
        downloadingAttachmentId,
        handleSelectFile,
        handleRemoveFile,
        handleAttachmentClick,
        clearSearch,
        searchTerm,
        selectedFile,
        handleFinalClick,
        filteredMessages,
        allAttachments,
    } = useGroupChat();

    const messageInputRef = useRef<HTMLTextAreaElement>(null);

    useMessagesSocket();

    /*
     * Scroll to the latest chat whenever the Group Chat tab
     * is selected.
     */
    useEffect(() => {
        if (!shouldScrollToBottom ||isLoadingGroupMessages ||!groupMessages?.length) {
            return;
        }

        requestAnimationFrame(() => {
            chatEndRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
        });
    }, [
        shouldScrollToBottom,
        isLoadingGroupMessages,
        groupMessages?.length,
        chatEndRef,
    ]);

    /*
     * Create preview URL for selected image attachment.
     */
    useEffect(() => {
        if (!selectedFile || !isImageFile(selectedFile.name)) {
            dispatch(clearChatValue('selectedFilePreviewUrl'));
            return;
        }

        const objectUrl = URL.createObjectURL(selectedFile);

        dispatch(setChatValue({key: 'selectedFilePreviewUrl', value: objectUrl,}));

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [selectedFile, dispatch]);

    /*
     * Auto-resize textarea.
     *
     * The textarea grows until 120px.
     * Once it reaches the maximum height, it becomes scrollable.
     * The scroll position is kept at the bottom so the newest line
     * remains visible while typing.
     */
    useEffect(() => {
        const textarea = messageInputRef.current;

        if (!textarea) return;

        const maxHeight = 120;

        textarea.style.height = 'auto';

        const contentHeight = textarea.scrollHeight;
        const newHeight = Math.min(contentHeight, maxHeight);

        textarea.style.height = `${newHeight}px`;

        const animationFrame = requestAnimationFrame(() => {
            if (contentHeight > maxHeight) {
                textarea.scrollTop = textarea.scrollHeight;
            } else {
                textarea.scrollTop = 0;
            }
        });

        return () => {
            cancelAnimationFrame(animationFrame);
        };
    }, [message]);

    const canSend = Boolean(message.trim()) || Boolean(selectedFile);
    const isComposerDisabled = isDraftProposal;
    const isSelectedFileImage = Boolean(selectedFile && isImageFile(selectedFile.name));

    return (
        <>
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-gray-200 bg-white">
                {/* CHAT HEADER */}
                <ChatsHeader
                    isSearchOpen={isSearchOpen}
                    searchTerm={searchTerm}
                    setSearchTerm={(value) => dispatch(setChatValue({key: 'searchTerm', value}))}
                    setIsSearchOpen={(value) => dispatch(setChatValue({key: 'isSearchOpen', value,}))}
                    setShowAttachments={(value) => dispatch(setChatValue({key: 'showAttachments', value}))}
                    allAttachments={allAttachments}
                    filteredMessages={filteredMessages}
                />

                {/* MESSAGES */}
                <div className={`min-h-0 flex-1 overflow-y-auto bg-white px-2.5 py-3 sm:px-4 
                        ${isLoadingGroupMessages || filteredMessages.length === 0 ? 'flex items-center justify-center' : 'space-y-5'
                    }`}>
                    {isLoadingGroupMessages ? (
                        <LoadingState />
                    ) : isErrorGroupMessages ? (
                        <ErrorState
                            parentStyle="flex h-auto w-full items-center justify-center px-6 py-8"
                        />
                    ) : filteredMessages.length === 0 ? (
                        <div className="w-full px-4 text-center">
                            <div className="mb-2 flex justify-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#186cb2]/10">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-6 w-6 text-[#186cb2]" >
                                        <circle cx="11" cy="11" r="7" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m20 20-4-4" />
                                    </svg>
                                </div>
                            </div>
                            <p className="text-xs font-semibold text-gray-800">
                                {searchTerm ? 'No messages found' : 'Start a quick conversation'}
                            </p>
                            <p className="mx-auto mt-1 max-w-xs text-[10px] leading-relaxed text-gray-500">
                                {searchTerm
                                    ? 'Try searching for another message, person, department, or attachment.'
                                    : 'Discuss proposal updates, requirements, and other details with your team.'}
                            </p>
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={clearSearch}
                                    className="mt-3 rounded-md bg-[#186cb2] px-3 py-1.5 text-[10px] font-medium text-white transition hover:bg-[#145d99]"
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    ) : (
                        filteredMessages.map((msg) => {
                            const isOutgoing = msg.user_id === userId;
                            const type = isOutgoing ? 'outgoing' : 'incoming';
                            const attachment = getMessageAttachment(msg);
                            return (
                                <MessageChat
                                    key={msg.comment_id}
                                    setMessage={(value) => dispatch(setChatValue({key: 'message', value}))}
                                    msg={msg}
                                    attachment={attachment}
                                    type={type}
                                    messageBgColor={type === 'outgoing' ? 'bg-gray-100' : 'bg-gray-50'}
                                    styleDiv={type === 'outgoing' ? 'justify-end' : 'justify-start'}
                                    isActive={activeMessageId === msg.comment_id}
                                    onToggle={() => dispatch(setChatValue({ key: 'activeMessageId', value: activeMessageId === msg.comment_id ? null : msg.comment_id }))}
                                    isDeleting={deletingMessageId === msg.comment_id}
                                    isLoadingDeleteComment={isLoadingDeleteComment}
                                    onDownloadAttachment={handleDownloadAttachment}
                                    isDownloadingAttachment={downloadingAttachmentId === msg.comment_id}
                                />
                            );
                        })
                    )}

                    {/* SENDING INDICATOR */}
                    {isLoadingAddCommentChat && (
                        <div className="flex justify-end">
                            <div className="flex items-center gap-1.5 rounded-lg rounded-br-sm bg-gray-100 px-3 py-1.5">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400" />
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400 [animation-delay:150ms]" />
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400 [animation-delay:300ms]" />
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                {/* COMPOSER */}
                <div className="shrink-0 border-t border-gray-200 bg-gray-50">
                    {/* EDITING MESSAGE */}
                    {isCancelUpdateMessage && (
                        <div className="flex items-center justify-between border-b border-gray-200 bg-white px-3 py-1.5">
                            <div className="flex min-w-0 items-center gap-1.5">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 shrink-0 text-[#186cb2]" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13l-2.68.805a4.5 4.5 0 0 1 1.13-1.897l8.922-8.941Z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125 16.875 4.5" />
                                </svg>
                                <p className="truncate text-[10px] font-medium text-gray-600">
                                    Editing message
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleCancelUpdateComment}
                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                                title="Cancel editing"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    )}

                    {/* SELECTED FILE */}
                    {selectedFile && (
                        <div className="border-b border-gray-200 bg-white px-2.5 py-2">
                            {isSelectedFileImage &&
                            selectedFilePreviewUrl ? (
                                <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                                    <div className="flex items-center justify-between border-b border-gray-200 bg-white px-2.5 py-1.5">
                                        <div className="flex min-w-0 items-center gap-1.5">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5 shrink-0 text-[#186cb2]" >
                                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m21 15-5-5L5 21" />
                                            </svg>
                                            <p className="truncate text-[10px] font-medium text-gray-600">
                                                {selectedFile.name}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                                            title="Remove image"
                                        >
                                            <CloseIcon />
                                        </button>
                                    </div>

                                    <div className="flex max-h-[220px] min-h-[100px] items-center justify-center overflow-hidden bg-gray-100 p-2 sm:max-h-[260px]">
                                        <img
                                            src={selectedFilePreviewUrl}
                                            alt={selectedFile.name}
                                            className="max-h-[200px] max-w-full rounded-md object-contain sm:max-h-[240px]"
                                        />
                                    </div>

                                    <div className="border-t border-gray-200 bg-white px-2.5 py-1.5">
                                        <p className="truncate text-[9px] text-gray-400">
                                            {(selectedFile.size / 1024 / 1024 ).toFixed(2)}{' '}MB
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10 text-[#186cb2]">
                                        {getFileIcon(selectedFile.name)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-[10px] font-medium text-gray-700">
                                            {selectedFile.name}
                                        </p>
                                        <p className="mt-0.5 text-[9px] text-gray-400">
                                            {(selectedFile.size / 1024 / 1024).toFixed(2)}{' '}MB
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleRemoveFile}
                                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-200 hover:text-gray-600"
                                        title="Remove attachment"
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* MESSAGE COMPOSER */}
                    <div className="p-2">
                        <div className="flex items-end gap-1.5 overflow-hidden">
                            {/* FILE INPUT */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleSelectFile}
                                disabled={isComposerDisabled || isUserMonitoring}
                            />

                            {/* ATTACHMENT BUTTON */}
                            <button
                                type="button"
                                onClick={handleAttachmentClick}
                                disabled={isComposerDisabled || isUserMonitoring}
                                title="Attach file"
                                aria-label="Attach file"
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition-all duration-150 hover:bg-[#186cb2]/10 hover:text-[#186cb2] active:scale-95 disabled:cursor-not-allowed disabled:text-gray-300"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]" >
                                    <path d="M13.5 6.5 7.75 12.25a3.182 3.182 0 0 0 4.5 4.5l6.5-6.5a4.243 4.243 0 0 0-6-6L5.5 12.5a5.303 5.303 0 0 0 7.5 7.5l5-5" />
                                </svg>
                            </button>
                            {/* MESSAGE TEXTAREA */}
                            <textarea
                                ref={messageInputRef}
                                disabled={isComposerDisabled}
                                value={message}
                                rows={1}
                                onChange={(e) => dispatch(setChatValue({key: 'message', value: e.target.value}))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();

                                        if (canSend && !isDraftProposal) {
                                            handleFinalClick();
                                        }

                                        return;
                                    }

                                    if (e.key === 'Enter' && e.shiftKey) {
                                        return;
                                    }
                                }}
                                placeholder={
                                    isDraftProposal
                                        ? 'Chat is unavailable for draft proposals'
                                        : isCancelUpdateMessage
                                            ? 'Edit your message...'
                                            : selectedFile
                                                ? 'Add a message or send attachment...'
                                                : 'Type a message...'
                                }
                                className="min-h-[40px] max-h-[120px] min-w-0 flex-1 resize-none overflow-y-auto rounded-[20px] border border-gray-300 bg-white px-4 py-2.5 text-xs leading-5 text-gray-800 outline-none transition [scrollbar-width:none] placeholder:text-gray-400 focus:border-[#186cb2] focus:ring-2 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:bg-gray-100 [&::-webkit-scrollbar]:hidden"
                            />

                            {/* SEND BUTTON */}
                            <button
                                type="button"
                                disabled={!canSend || isDraftProposal || isUserMonitoring}
                                onClick={handleFinalClick}
                                title={isCancelUpdateMessage ? 'Update message' : 'Send message'}
                                aria-label={isCancelUpdateMessage ? 'Update message' : 'Send message'}
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#186cb2] text-white shadow-sm transition-all duration-150 hover:bg-[#145d99] hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="h-[16px] w-[16px] translate-x-[0.5px]" >
                                    <path d="m21 3-7.5 18-3.75-7.75L2 9.5 21 3Z" />
                                    <path d="m21 3-11.25 10.25" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <ChatAttachmentPreview/>
            {/* ATTACHMENTS */}
            <ChatAttachmentModal
                isOpen={showAttachments}
                attachments={allAttachments}
                onClose={() => dispatch(clearChatValue('showAttachments'))}
                onDownload={handleDownloadAttachment}
            />
            {/* DELETE CONFIRMATION */}
            {isOpenDeleteModal && (
                <ConfirmDefaultModal
                    modalTitle="Delete this message?"
                    modalSubTitle="This message will be permanently deleted. Are you sure you want to continue?"
                    onCancel={handleClose}
                    onConfirm={handleDeleteComment}
                    isLoading={isLoadingDeleteComment}
                />
            )}
        </>
    );
}